import React, {useEffect, useRef, useState} from 'react'
import {Loading} from '@therms/atalaya'
import {
  Alerts,
  AvailabilityChangeRequest,
  NoResults,
  PreferredDay,
  RegionName,
  SchedulingUserAvailability,
  TimeOffRequest,
  toSavedAvailability,
  useAvailabilityZones,
  useCurrentUser,
  useOrganizationSchedulingSettings,
  useRPCContext,
  useRPCServerEventHandler,
  useScheduleConfig,
  useSchedulingUser,
} from '@therms/react-modules'
import {RPCDocs} from '@therms/rpc-gateway'
import {EditAvailabilityModal} from '@src/modules/Scheduling/components/EditAvailabilityModal'
import {
  RequestTimeOffModal,
  TimeOffDraft,
} from '@src/modules/Scheduling/components/RequestTimeOffModal'
import {SchedulingTabView} from '@src/modules/Scheduling/components/SchedulingTabView'

// shortcut: one page of each; the screen shows upcoming time off and a few
// recent availability decisions, so older rows can drop off. Page if needed.
const TIME_OFF_LIMIT = 200
const AVAILABILITY_CHANGES_LIMIT = 20

/**
 * Runs one load at a time; a call made meanwhile reruns it once that ends.
 * The RPC client merges identical calls in flight, so a reload after a change
 * would otherwise get the answer from before it.
 */
function useSerialLoad(load: () => Promise<void>) {
  const stateRef = useRef({again: false, running: false})
  const loadRef = useRef(load)
  loadRef.current = load

  return function run() {
    if (stateRef.current.running) {
      stateRef.current.again = true
      return
    }
    stateRef.current.running = true
    loadRef.current().finally(() => {
      stateRef.current.running = false
      if (stateRef.current.again) {
        stateRef.current.again = false
        run()
      }
    })
  }
}

function SchedulingIndex() {
  const user = useCurrentUser()
  const {rpcService} = useRPCContext()

  // Remote-first: changes are built on top of this, and the server replaces
  // availability wholesale, so a stale cached copy would undo a saved edit.
  const {
    data: schedulingUser,
    loading,
    refresh: refreshSchedulingUser,
  } = useSchedulingUser(user.id, {cacheStrategy: 'remote-first'})
  // Only for the first day of the week; the self-service switches are the org's.
  const {data: scheduleConfig} = useScheduleConfig({})
  const {data: settings} = useOrganizationSchedulingSettings()
  // Stored in the primary region's clock; shown and entered in the device's.
  const zones = useAvailabilityZones(user.primaryRegionId)

  const [availabilityOverride, setAvailabilityOverride] =
    useState<SchedulingUserAvailability | null>(null)
  const [availabilityChanges, setAvailabilityChanges] = useState<
    AvailabilityChangeRequest[]
  >([])
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([])
  const [timeOffLoaded, setTimeOffLoaded] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  // `null` files a new request; a request opens it for editing.
  const [timeOffModal, setTimeOffModal] = useState<TimeOffRequest | null>()

  const loadTimeOff = useSerialLoad(() =>
    rpcService.Scheduling.GetUserTimeOffRequests.v1
      .call({pagination: {limit: TIME_OFF_LIMIT}, userId: user.id})
      .then(({data}) => {
        setTimeOffRequests(data.requests as unknown as TimeOffRequest[])
      })
      .catch((err) => {
        Alerts.error('Error loading time off requests', err.message)
      })
      .finally(() => setTimeOffLoaded(true)),
  )

  // No userId: a user's own changes, newest first.
  const loadAvailabilityChanges = useSerialLoad(() =>
    rpcService.Scheduling.GetUserAvailabilityChangeRequests.v1
      .call({pagination: {limit: AVAILABILITY_CHANGES_LIMIT}})
      .then(({data}) => {
        setAvailabilityChanges(
          data.requests as unknown as AvailabilityChangeRequest[],
        )
      })
      .catch((err) => {
        Alerts.error('Error loading availability changes', err.message)
      }),
  )

  useEffect(() => {
    loadTimeOff()
    loadAvailabilityChanges()
  }, [])

  useRPCServerEventHandler(
    'SchedulingUserTimeOffRequestStatusChanged',
    (_event, payload) => {
      if (payload.userId === user.id) loadTimeOff()
    },
  )

  // A manager's decision changes both the request and, when approved, the
  // live availability.
  useRPCServerEventHandler(
    'SchedulingUserAvailabilityChangeStatusChanged',
    (_event, payload) => {
      if (payload.userId !== user.id) return
      loadAvailabilityChanges()
      setAvailabilityOverride(null)
      refreshSchedulingUser().catch(() => null)
    },
  )

  const availability =
    availabilityOverride ??
    (schedulingUser?.availability as unknown as
      SchedulingUserAvailability | undefined)

  const pendingRequest = availabilityChanges.find(
    (change) => change.status === 'pending',
  )

  const toLocal = (a?: SchedulingUserAvailability) =>
    a && {...a, unavailableTimes: zones.toLocal(a.unavailableTimes)}

  async function submitAvailability(draft: SchedulingUserAvailability) {
    const requested = toSavedAvailability(
      draft,
    ) as unknown as RPCDocs.Scheduling.SubmitUserAvailabilityChange.v1.Requested

    // One pending change at a time: while there is one, edit it.
    if (pendingRequest) {
      await rpcService.Scheduling.UpdateUserAvailabilityChange.v1.call({
        id: pendingRequest.id,
        requested,
      })
      loadAvailabilityChanges()
      Alerts.success('Change request updated')
      return
    }

    const {data} =
      await rpcService.Scheduling.SubmitUserAvailabilityChange.v1.call({
        requested,
      })

    if (data.applied) {
      setAvailabilityOverride(
        (data.schedulingUser?.availability ??
          draft) as unknown as SchedulingUserAvailability,
      )
      // Refreshes the cached copy too, which other screens read cache-only.
      refreshSchedulingUser().catch(() => null)
      Alerts.success('Availability updated')
    } else if (data.request) {
      Alerts.success('Change request submitted for approval')
    }
    loadAvailabilityChanges()
  }

  async function withdrawAvailabilityChange(change: AvailabilityChangeRequest) {
    try {
      await rpcService.Scheduling.WithdrawUserAvailabilityChange.v1.call({
        id: change.id,
      })
      loadAvailabilityChanges()
      Alerts.success('Change request withdrawn')
    } catch (err: any) {
      Alerts.error('There was a problem withdrawing the change', err.message)
    }
  }

  function changePreferredDays(days: PreferredDay[]) {
    const baseline = pendingRequest
      ? pendingRequest.requested
      : (availability ?? {})

    submitAvailability({...baseline, preferredDays: days}).catch((err: any) => {
      Alerts.error('There was a problem updating preferred days', err.message)
    })
  }

  async function saveTimeOff(draft: TimeOffDraft) {
    const type =
      draft.type as unknown as RPCDocs.Scheduling.RequestUserTimeOff.v1.Type

    if (timeOffModal) {
      await rpcService.Scheduling.UpdateUserTimeOffRequest.v1.call({
        endTime: draft.endTime,
        id: timeOffModal.id,
        startTime: draft.startTime,
        type: type as unknown as RPCDocs.Scheduling.UpdateUserTimeOffRequest.v1.Type,
      })
      Alerts.success('Time off request updated')
    } else {
      await rpcService.Scheduling.RequestUserTimeOff.v1.call({
        endTime: draft.endTime,
        startTime: draft.startTime,
        type,
      })
      Alerts.success('Time off requested')
    }
    loadTimeOff()
  }

  async function withdrawTimeOff(request: TimeOffRequest) {
    try {
      const {data} =
        await rpcService.Scheduling.WithdrawUserTimeOffRequest.v1.call({
          id: request.id,
        })
      loadTimeOff()
      Alerts.success(
        data.request.withdrawalRequestedAt
          ? 'Withdrawal sent for approval'
          : 'Time off withdrawn',
      )
    } catch (err: any) {
      Alerts.error('There was a problem withdrawing the request', err.message)
    }
  }

  if (loading && !schedulingUser) {
    return <Loading overlay size="lg" />
  }

  // The tab is hidden for these, but the identity flag it reads can lag a
  // scheduling user switched off since sign-in.
  if (!schedulingUser?.active) {
    return <NoResults text="No scheduling profile for your account" />
  }

  const firstDayOfWeek = scheduleConfig?.settings?.firstDayOfWeek
  // The server refuses availability writes without a primary region, and
  // blocks can't be converted until its zone is known.
  const needsPrimaryRegion =
    !!settings?.allowUserAvailabilityChange && !user.primaryRegionId
  const canEditAvailability =
    !!settings?.allowUserAvailabilityChange &&
    !needsPrimaryRegion &&
    !zones.loading

  return (
    <div className="p-base">
      {needsPrimaryRegion && (
        <div className="text-color-subtle mx-auto mb-base max-w-[760px] text-sm">
          To change your availability, ask a manager to set your primary
          region. Your times follow its clock.
        </div>
      )}
      <SchedulingTabView
        availability={toLocal(availability)}
        availabilityChanges={availabilityChanges.map((change) => ({
          ...change,
          requested: toLocal(change.requested) ?? change.requested,
        }))}
        canEditAvailability={canEditAvailability}
        // Until the list is in, a pending request can't be seen to hide the button.
        canRequestTimeOff={!!settings?.allowUserTimeOffRequest && timeOffLoaded}
        currentUserId={user.id}
        daysZoneLabel={
          zones.differs && user.primaryRegionId ? (
            <RegionName regionId={user.primaryRegionId} />
          ) : undefined
        }
        firstDayOfWeek={firstDayOfWeek}
        onChangePreferredDays={changePreferredDays}
        onEditAvailability={() => setShowEditModal(true)}
        onEditTimeOff={setTimeOffModal}
        onRequestTimeOff={() => setTimeOffModal(null)}
        onWithdrawAvailabilityChange={withdrawAvailabilityChange}
        onWithdrawTimeOff={withdrawTimeOff}
        timeOffRequests={timeOffRequests}
        user={{
          avatarUrl: user.avatarUrl,
          displayName: user.displayName,
          title: user.title,
        }}
      />

      {showEditModal && (
        <EditAvailabilityModal
          availability={toLocal(
            pendingRequest ? pendingRequest.requested : availability,
          )}
          closeHandler={() => setShowEditModal(false)}
          firstDayOfWeek={firstDayOfWeek}
          onSubmit={(draft) =>
            submitAvailability({
              ...draft,
              unavailableTimes: zones.toStored(draft.unavailableTimes),
            })
          }
          storedTimes={
            zones.differs && user.primaryRegionId
              ? {
                  convert: zones.toStored,
                  zoneLabel: <RegionName regionId={user.primaryRegionId} />,
                }
              : undefined
          }
        />
      )}

      {timeOffModal !== undefined && (
        <RequestTimeOffModal
          closeHandler={() => setTimeOffModal(undefined)}
          onSubmit={saveTimeOff}
          request={timeOffModal ?? undefined}
        />
      )}
    </div>
  )
}

export {SchedulingIndex}
